import { useState } from 'react'
import { useAppDispatch } from '../app/typedReduxHooks'
import useApi from '../common/utils/api/useApi'
import { PrescribedItem } from '../types'
import { setScriptState } from '../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'
import { createPrescriberScriptNumber } from '../pages/NewScriptPage/ScriptForm/PrescribedItem/services'
import { StepValidationStatus } from '../types/hi-validation'

interface UseValidateScriptNoParams {
  drugs: (PrescribedItem | undefined)[]
  onError?: (error: string) => void
}

export const useValidateScriptNo = ({ drugs, onError }: UseValidateScriptNoParams) => {
  const dispatch = useAppDispatch()
  const [isScriptNoValid, setIsScriptNoValid] = useState<StepValidationStatus>('NOT_START')

  const {
    trigger: generateScriptNo,
    loading: isGeneratingScriptNo,
    error: scriptNoGenerationError,
    reset: resetGeneratingScriptNoRequest,
  } = useApi(createPrescriberScriptNumber)

  const validateScriptNumber = async (): Promise<(PrescribedItem | undefined)[] | false> => {
    // Early return if all drugs already have script numbers
    const isEveryDrugHasScriptNumber = drugs
      .filter((drug) => !!drug)
      .every((drug) => drug?.prescriberScriptNumber)
    if (isEveryDrugHasScriptNumber) {
      setIsScriptNoValid('VALID')
      return drugs
    }

    try {
      // Generate all missing script numbers in parallel, then do a single redux update.
      const updatedPrescribedItemList = await Promise.all(
        drugs.map(async (drug, index): Promise<PrescribedItem | undefined> => {
          try {
            if (!drug || drug.prescriberScriptNumber) return drug

            const result = await generateScriptNo(undefined)
            if (!result?.prescriberScriptNumber) {
              throw new Error(`Failed to generate script number for drug at index ${index}`)
            }

            return {
              ...drug,
              prescriberScriptNumber: result.prescriberScriptNumber,
            }
          } catch (error) {
            // Capture which specific drug failed
            throw new Error(
              `Failed to generate script number for drug at position ${index + 1}: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            )
          }
        })
      )

      // After this dispatch returns, the redux store already contains the updated list.
      dispatch(setScriptState({ prescribedItemList: updatedPrescribedItemList }))

      setIsScriptNoValid('VALID')
      return updatedPrescribedItemList
    } catch (error) {
      setIsScriptNoValid('INVALID')
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to generate script numbers'
      onError?.(errorMessage)
      return false
    }
  }

  return {
    validateScriptNumber,
    isScriptNoValid,
    isGeneratingScriptNo,
    scriptNoGenerationError,
    resetGeneratingScriptNoRequest,
  }
}
